import React, { useMemo, useState, useEffect } from 'react';
import { DollarSign, Search, X, MoreHorizontal } from 'lucide-react';
import CollapsibleServiceGroup from '../shared/CollapsibleServiceGroup';
import { FUNDO_QUITADO, estaQuitado } from '../shared/EstadoDoDinheiro';
import { calcularEntrada, entradaAEmitir } from '../../../utils/calculations/entrada';
import {
  GRUPOS_FINANCEIROS, APRESENTACAO_DO_GRUPO, grupoDoServico,
} from '../../../utils/calculations/agruparFinanceiro';
import { criticidadeDoServico } from '../../../utils/calculations/prazos';
import { situacaoNoContexto } from '../../../utils/calculations/proximoPasso';
import { MARK } from '../../../constants';
import { nomeDoCliente } from '../../../data/clients';
import ServiceLabel from '../shared/ServiceLabel';
import ObservacaoDeCobranca from '../shared/ObservacaoDeCobranca';
import {
  listarSaldoPorCliente, listarParcelasEmAberto, resumoDoSaldo, agendaDeRecebimento,
  listarRecebidoNoTempo, recebidoPorMes,
} from '../../../data/recebimentos';

const FinancialTab = ({
  services,
  currentLab,
  calculateServiceValue,
  getServiceStatus,
  canManageServices,
  canEdit,
  openCostsModal,
  openModal,
  handleBillService,
  handleChargeEntrada,
  entradaPagaPorServico = {},
  entradaEmitidaPorServico = {},
  generateBillingPDF,
  clients,
  handleMarkAsPaid,
  handleRefaturar,
  handleRefaturarEntrada,
  handleTogglePago,
  formaPorServico = {},
  parcelasPorServico = {},
  // A conta de cada serviço: cobrado, recebido, falta, próximo vencimento.
  // Soma das faturas VIVAS daquele serviço, o que já implementa a supersessão
  // da entrada sem nenhum caso especial na tela.
  contaPorServico = {},
  // Muda a cada recarga das parcelas. É o sinal de que algo financeiro
  // aconteceu, e é o que faz o painel de saldo no topo acompanhar os cartões.
  versaoDasParcelas = 0,
  loading,
  generateServiceGroupPDF,
  // Muda a cada vez que se entra na aba. Os grupos e a lista de quem deve
  // fecham de novo: a aba chega sempre resumida, sem o que ficou aberto da
  // última visita.
  entrada = 0,
  salvarObservacaoDeCobranca,
}) => {
  /**
   * A BUSCA, por cliente ou serviço.
   *
   * Filtra os serviços ANTES de agrupar, então os grupos, as somas do
   * cabeçalho e a linha do pé passam a falar só do que foi achado. Com busca,
   * os grupos abrem: fechados, o resultado ficaria escondido atrás de um
   * clique por grupo. Some a cada entrada na aba, como o resto que fecha.
   */
  const [busca, setBusca] = useState('');
  useEffect(() => { setBusca(''); }, [entrada]);
  const termo = semAcento(busca);
  const buscando = termo.length > 0;

  const servicosAchados = useMemo(() => {
    if (!buscando) return services;
    return services.filter(service => semAcento([
      nomeDoCliente(service, clients),
      clients.find(c => c.id === service.client_id)?.contact,
      service.service_code,
      // "dia 15" acha quem prometeu pagar no dia 15.
      service.observacao_de_cobranca,
      ...(parcelasPorServico[service.id] || []).map(p => p.numeroDaFatura),
    ].filter(Boolean).join(' ')).includes(termo));
  }, [services, clients, parcelasPorServico, termo, buscando]);

  /**
   * A criticidade de um serviço, com tudo que ela precisa saber sobre a entrada.
   *
   * Num lugar só porque a tela pergunta isto duas vezes — para agrupar e para
   * escrever o motivo no cartão —, e as duas têm que concordar. Enquanto os
   * argumentos estavam repetidos, esquecer um deles em UMA delas sumia com o
   * motivo do cartão sem tirar o serviço do grupo crítico.
   */
  const criticidade = (service) => {
    const emitida = !!entradaEmitidaPorServico?.[service.id];
    return criticidadeDoServico(service, {
      parcelas: parcelasPorServico[service.id] || [],
      valor: calculateServiceValue(service),
      entradaPaga: entradaPagaPorServico[service.id] || 0,
      entradaEmitida: emitida,
      entradaAEmitir: entradaAEmitir(service, currentLab, emitida),
    });
  };

  /**
   * Os serviços pela AÇÃO que cada um pede, e quantos não pedem nenhuma.
   *
   * `foraDoFinanceiro` não é um resto esquecido: é serviço em andamento, sem
   * nada emitido e sem nada a emitir. Ele lotava o grupo "Em Andamento", cuja
   * única cifra era uma projeção que em `por_prenhez` sem DG60 vale zero. Sai
   * da lista e vira uma linha no pé, porque ninguém pode desaparecer em
   * silêncio desta tela.
   */
  const groupedServices = useMemo(() => {
    const groups = Object.fromEntries(GRUPOS_FINANCEIROS.map(g => [g, []]));
    let fora = 0;

    servicosAchados.forEach(service => {
      const grupo = grupoDoServico({
        motivos: criticidade(service).motivos,
        billed: !!service.billed,
        paid: !!service.paid,
        finalizado: getServiceStatus(service) === 'Finalizado',
        teFeita: !!service.te_done,
        saldo: contaPorServico[service.id]?.saldo || 0,
      });
      if (grupo) groups[grupo].push(service); else fora += 1;
    });

    return { ...groups, foraDoFinanceiro: fora };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicosAchados, getServiceStatus, calculateServiceValue, parcelasPorServico,
      entradaPagaPorServico, contaPorServico]);

  /**
   * O dinheiro de cada grupo, e o que ele SIGNIFICA ali.
   *
   * O cabeçalho mostrava só a contagem. Numa aba de dinheiro, "Concluídos - A
   * Faturar (3)" não responde a pergunta que fez a pessoa abrir a aba, e o
   * total sempre existiu — só não estava na tela.
   *
   * O número muda de grupo para grupo porque a pergunta muda: em quem já foi
   * faturado interessa o que FALTA receber; em quem não foi, o que há A
   * FATURAR; em quem se encerrou, o que ENTROU.
   */
  const dinheiroDoGrupo = (grupo, lista) => {
    const soma = (f) => lista.reduce((t, x) => t + Math.round((f(x) || 0) * 100), 0) / 100;
    const conta = (x) => contaPorServico[x.id] || {};

    // A ENTRADA JÁ EMITIDA de quem ainda não foi faturado. Aparece em segundo
    // plano onde convive com outro dinheiro: era o valor invisível da aba,
    // porque o cabeçalho mostrava só "a faturar".
    const entradaEmAberto = soma(x => (x.billed ? 0 : conta(x).saldo));

    switch (grupo) {
      // O que já venceu. Soma o saldo de quem foi faturado com o valor de quem
      // nunca virou fatura: as duas naturezas são dinheiro parado.
      case 'aCobrar':
        return {
          valor: soma(x => (x.billed ? conta(x).saldo : calculateServiceValue(x))),
          texto: 'Vencido',
          entrada: entradaEmAberto,
        };
      case 'aFaturar':
        return {
          valor: soma(x => (x.billed ? 0 : calculateServiceValue(x))),
          texto: 'A emitir',
          entrada: entradaEmAberto,
        };
      case 'aReceber':
        return { valor: soma(x => conta(x).saldo), texto: 'Falta receber' };
      // O que já entrou adiantado. O que falta faturar ainda não é número: num
      // `por_prenhez` sem DG ele vale zero, e somá-lo aqui seria projeção.
      case 'emDia':
        return { valor: soma(x => conta(x).recebido), texto: 'Entrada recebida' };
      case 'encerrado':
        return { valor: soma(x => conta(x).recebido), texto: 'Recebido' };
      default:
        return null;
    }
  };

  const rotuloDoDinheiro = (grupo, lista) => {
    const d = dinheiroDoGrupo(grupo, lista);
    if (!d) return {};
    const reais = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    return {
      ...(d.valor > 0 ? { valor: reais(d.valor), valorTitulo: `${d.texto} neste grupo` } : {}),
      ...(d.entrada > 0 ? { segundoValor: `+ ${reais(d.entrada)} de entrada` } : {}),
    };
  };

  /**
   * As três respostas de um serviço — entrada, serviço, próximo passo — da
   * regra testada em `proximoPasso`. A tela só desenha.
   */
  const situacao = (service) => situacaoNoContexto(service, {
    parcelasPorServico, entradaEmitidaPorServico, laboratory: currentLab, services,
    valor: calculateServiceValue(service),
    motivos: criticidade(service).motivos,
  });

  /**
   * O MENU ⋯: tudo o que não é o próximo passo.
   *
   * O cartão tinha até oito botões no mesmo peso — custos, cobrar entrada, PDF
   * da entrada, faturar, PDF, refaturar, marcar pago e um por parcela — e o
   * que era para fazer AGORA se perdia entre eles. O passo virou o único botão
   * da linha; o resto mora aqui, com o nome por extenso.
   */
  const [menuAberto, setMenuAberto] = useState(null);
  // O serviço cuja observação está sendo escrita, aberta pelo ⋯.
  const [anotando, setAnotando] = useState(null);
  useEffect(() => {
    if (!menuAberto) return undefined;
    const fechar = (e) => { if (!e.target.closest('[data-menu-financeiro]')) setMenuAberto(null); };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [menuAberto]);
  useEffect(() => { setMenuAberto(null); }, [entrada]);

  const pdfDaEntrada = (service) => {
    const daEntrada = (parcelasPorServico[service.id] || []).filter(p => p.ehEntrada);
    // Reimpressão usa a data em que a fatura SAIU, não a de hoje.
    generateBillingPDF(service, currentLab, clients, {
      modo: 'entrada',
      billingDate: daEntrada[0].emitidaEm || new Date().toISOString().split('T')[0],
      notes: '',
      parcelas: daEntrada.map(p => ({ numero: p.numero, valor: p.valor, vencimento: p.vencimento })),
      entradaAbatida: 0,
    });
  };

  const pdfDaFatura = (service) => generateBillingPDF(service, currentLab, clients, {
    billingDate: service.billed_date || new Date().toISOString().split('T')[0],
    notes: service.billing_notes || '',
    formaPagamento:    formaPorServico[service.id]?.forma,
    formaPagamentoObs: formaPorServico[service.id]?.obs,
    // O abatimento da entrada não vai: o valor guardado já é o líquido. E
    // `!ehEntrada` porque a entrada tem documento próprio.
    parcelas: (parcelasPorServico[service.id] || [])
      .filter(p => !p.ehEntrada)
      .map(p => ({ numero: p.numero, valor: p.valor, vencimento: p.vencimento })),
  });

  const executar = (service, passo) => {
    if (passo.acao === 'emitirEntrada') handleChargeEntrada(service);
    else if (passo.acao === 'faturar') handleBillService(service);
    else if (passo.acao === 'receber') handleMarkAsPaid(service, passo.parcela);
  };

  // Quem pode o quê: emitir e faturar são de quem gere serviço; receber, de
  // quem edita o financeiro. Sem permissão o passo continua ESCRITO — saber o
  // que falta é útil mesmo para quem não é quem faz.
  const podeExecutar = (passo) => (passo.acao === 'receber' ? canEdit() : canManageServices());

  const itensDoMenu = (service, passo) => {
    const parcelas = parcelasPorServico[service.id] || [];
    const valor = calculateServiceValue(service);
    const reaisCurto = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    const itens = [];
    const gere = canManageServices();

    if (gere && passo?.acao !== 'emitirEntrada' && entradaEmitidaPorServico !== null
      && entradaAEmitir(service, currentLab, Boolean(entradaEmitidaPorServico?.[service.id])).deve) {
      itens.push({ rotulo: `Emitir entrada (${calcularEntrada(service, currentLab).quantidade} embriões)`,
        fazer: () => handleChargeEntrada(service) });
    }
    if (gere && !service.billed && valor > 0 && passo?.acao !== 'faturar') {
      itens.push({ rotulo: 'Faturar o serviço', fazer: () => handleBillService(service) });
    }
    if (canEdit()) {
      parcelas.forEach(p => {
        if (passo?.acao === 'receber' && passo.parcela?.id === p.id) return;
        const doc = p.ehEntrada ? 'entrada' : 'serviço';
        const qual = p.total > 1 ? ` ${p.numero}/${p.total}` : '';
        itens.push({
          rotulo: p.paga_em
            ? `Recebimentos da ${doc}${qual} (quitada)`
            : `Receber ${doc}${qual} · R$ ${reaisCurto(p.valor)}`,
          fazer: () => handleMarkAsPaid(service, p),
        });
      });
    }
    if (gere && parcelas.some(p => p.ehEntrada)) {
      itens.push({ rotulo: 'PDF da entrada', fazer: () => pdfDaEntrada(service) });
    }
    if (service.billed) itens.push({ rotulo: 'PDF da fatura', fazer: () => pdfDaFatura(service) });
    // Cancelar documento contra o qual entrou dinheiro é decisão do
    // financeiro, não efeito colateral de corrigir um valor. Cada fatura olha
    // as PRÓPRIAS parcelas: olhava todas, e a entrada paga escondia o
    // refaturar do serviço, que nem mexe nela.
    const doServico = parcelas.filter(p => !p.ehEntrada);
    const daEntrada = parcelas.filter(p => p.ehEntrada);
    if (gere && service.billed && !doServico.some(p => p.paga_em)) {
      itens.push({ rotulo: 'Refaturar (cancela a fatura e volta para A faturar)', fazer: () => handleRefaturar(service) });
    }
    if (gere && handleRefaturarEntrada && daEntrada.length > 0 && !daEntrada.some(p => p.paga_em)) {
      itens.push({ rotulo: 'Refaturar entrada (apaga a entrada e volta para a emitir)', fazer: () => handleRefaturarEntrada(service) });
    }
    if (canEdit() && service.billed) {
      itens.push({
        rotulo: service.paid ? 'Desmarcar pago (apaga os recebimentos lançados)' : 'Marcar o serviço inteiro como pago',
        fazer: () => handleTogglePago(service),
      });
    }
    if (gere && !service.billed) {
      itens.push({ rotulo: 'Lançar custos', fazer: () => openCostsModal(service) });
    }
    if (canEdit() && salvarObservacaoDeCobranca) {
      itens.push({
        rotulo: service.observacao_de_cobranca
          ? 'Editar observação'
          : 'Anotar observação (ex.: vai pagar dia 15)',
        fazer: () => setAnotando(service.id),
      });
    }
    return itens;
  };

  const reais = (n) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  /** Uma coluna: estado por marca e texto, valor, e de onde ele vem. */
  const celula = (rotulo, c) => (
    <div className="min-w-0">
      {/* No celular as colunas viram linhas, e cada uma precisa dizer o que é. */}
      <p className="text-xs text-slate-400 lg:hidden">{rotulo}</p>
      <p className={`text-sm ${c.apagado ? 'text-slate-400' : 'text-slate-800'}`}>
        {c.marca ? `${c.marca} ` : ''}{c.texto}
        {c.valor > 0 && !c.apagado && (
          <span className="font-semibold text-slate-900 tabular-nums"> · R$ {reais(c.valor)}</span>
        )}
      </p>
      {c.detalhe && <p className="text-xs text-slate-500 truncate">{c.detalhe}</p>}
    </div>
  );

  /**
   * A linha de um serviço: quem, entrada, serviço, próximo passo.
   *
   * Colunas fixas, e nada posicionado por cima de nada. Os motivos eram selos
   * em `absolute` no canto do cartão, desenhados em cima justamente do valor
   * e do prazo — era a sobreposição. Agora o motivo É a coluna: "⚠️ vencida
   * há 7 dias" está na entrada ou no serviço, onde ele acontece.
   *
   * Função de render, não componente: declarado aqui dentro, viraria um tipo
   * novo a cada render e o React remontaria todas as linhas.
   */
  const renderLinha = (service, sit) => {
    const { entrada: ent, servico: srv, passo } = sit;
    const calculatedValue = calculateServiceValue(service);
    const contato = clients.find(c => c.id === service.client_id)?.contact;
    const itens = itensDoMenu(service, passo);
    const mudouDepoisDeFaturar = service.billed
      && Math.abs(calculatedValue - (parseFloat(service.billed_value) || 0)) > 0.005;

    return (
      <div key={service.id}
        // O grupo não corta mais o que passa da borda (para o menu ⋯ caber), e
        // o fundo de quitado da última linha cobria o canto arredondado.
        className={`px-4 py-3 last:rounded-b-lg ${estaQuitado(service) ? FUNDO_QUITADO : ''}`}>
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:gap-4 lg:items-center">
          {/* Quem: o nome abre o serviço, como antes. */}
          <button onClick={() => openModal && openModal('service', service)}
            className="min-w-0 text-left group">
            <p className="text-sm font-medium text-slate-900 truncate group-hover:underline">
              {nomeDoCliente(service, clients)}
            </p>
            {contato && <p className="text-xs text-slate-500 truncate">{contato}</p>}
            <ServiceLabel service={service} />
          </button>

          {celula('Entrada', ent)}
          {celula('Serviço', srv)}

          {/* O próximo passo, e o resto no ⋯. */}
          <div className="flex items-center gap-2 lg:justify-end" data-menu-financeiro>
            {passo ? (
              podeExecutar(passo) ? (
                <button onClick={() => executar(service, passo)} disabled={loading}
                  className="min-h-[36px] px-3 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">
                  {passo.rotulo}
                </button>
              ) : (
                <span className="text-sm text-slate-600 whitespace-nowrap">A fazer: {passo.rotulo.toLowerCase()}</span>
              )
            ) : (
              <span className="text-sm text-slate-400 whitespace-nowrap">
                {srv.urgencia === 'ok' ? 'concluído' : 'nada a fazer agora'}
              </span>
            )}

            {itens.length > 0 && (
              <div className="relative">
                <button onClick={() => setMenuAberto(m => (m === service.id ? null : service.id))}
                  aria-label="Mais ações" aria-expanded={menuAberto === service.id}
                  className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                  <MoreHorizontal size={16} />
                </button>
                {menuAberto === service.id && (
                  <div className="absolute right-0 top-full mt-1 z-20 w-72 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                    {itens.map(item => (
                      <button key={item.rotulo} disabled={loading}
                        onClick={() => { setMenuAberto(null); item.fazer(); }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        {item.rotulo}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* O PAPEL ficou atrás da conta: ela segue o cadastro, e o que
            envelheceu é o documento que foi ao cliente. Linha própria, embaixo
            — não por cima de nada. */}
        {mudouDepoisDeFaturar && (
          <p className="text-xs text-slate-600 mt-2">
            {MARK.WARN} A conta usa R$ {reais(calculatedValue)}, do cadastro, e o documento
            emitido diz R$ {reais(parseFloat(service.billed_value) || 0)}.
          </p>
        )}

        {(service.observacao_de_cobranca || anotando === service.id) && (
          <ObservacaoDeCobranca
            valor={service.observacao_de_cobranca}
            podeMexer={canEdit() && !!salvarObservacaoDeCobranca}
            onSalvar={(texto) => salvarObservacaoDeCobranca(service, texto)}
            editandoDeInicio={anotando === service.id}
            onFimDaEdicao={() => setAnotando(null)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-baseline gap-3">
        <h2 className="text-2xl font-bold text-slate-900">Financeiro</h2>
        <p className="text-sm text-slate-500">{currentLab?.name}</p>
      </div>

      {/*
        * O RECORTE DE TEMPO SAIU DAQUI, junto dos três livros e da barra de
        * proporção. Eles respondiam "como foi o mês", que é pergunta de
        * relatório: a resposta não muda nada do que se faz hoje, e ocupava o
        * topo da tela que existe para cobrar. Moraram aqui enquanto não havia
        * outro lugar; agora moram em Relatórios, com o período de lá.
        *
        * Ficou o que a cobrança usa: quanto falta entrar, quanto já venceu, e
        * quem deve.
        */}
      <PainelDeSaldo
        laboratoryId={currentLab?.id}
        recarregar={versaoDasParcelas}
        clientes={clients}
        entrada={entrada}
      />

      {services.length > 0 && (
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar cliente, serviço ou fatura..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {busca && (
            <button onClick={() => setBusca('')} title="Limpar a busca"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={13} />
            </button>
          )}
        </div>
      )}

      {services.length > 0 && buscando && servicosAchados.length === 0 ? (
        <p className="text-sm text-slate-500 px-1">Nenhum serviço encontrado para "{busca.trim()}".</p>
      ) : services.length > 0 ? (
        <div className="space-y-3">
          {GRUPOS_FINANCEIROS.map(grupo => {
            const lista = groupedServices[grupo] || [];
            if (lista.length === 0) return null;
            const { titulo, apoio } = APRESENTACAO_DO_GRUPO[grupo];

            return (
              <CollapsibleServiceGroup
                key={`${grupo}-${entrada}-${buscando}`}
                title={titulo}
                subtitulo={apoio}
                count={lista.length}
                {...rotuloDoDinheiro(grupo, lista)}
                // Todos fecham ao entrar: o cabeçalho já diz quantos e quanto,
                // e é dali que se decide o que abrir. Abrir A cobrar e A
                // faturar sozinhos foi desfeito a pedido (05/10). Só a busca
                // abre, para o resultado não ficar escondido.
                defaultExpanded={buscando}
                onExport={() => generateServiceGroupPDF(lista, `${titulo} — ${apoio}`)}
              >
                {/* Cabeçalho das colunas, uma vez por grupo, só onde há colunas. */}
                <div className="hidden lg:grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:gap-4 px-4 py-2 text-xs font-medium text-slate-500 border-b border-slate-200">
                  <span>Cliente · serviço</span><span>Entrada</span><span>Serviço</span>
                  <span className="text-right">Próximo passo</span>
                </div>
                <div className="divide-y divide-slate-200">
                  {/* O que vence primeiro em cima: cobrança é sobre o que
                      envelhece, não sobre a ordem de cadastro. */}
                  {lista
                    .map(service => ({ service, sit: situacao(service) }))
                    .sort((a, b) => (a.sit.ordem < b.sit.ordem ? -1 : a.sit.ordem > b.sit.ordem ? 1 : 0))
                    .map(({ service, sit }) => renderLinha(service, sit))}
                </div>
              </CollapsibleServiceGroup>
            );
          })}

          {/*
            * Quem não entrou em grupo nenhum, contado.
            *
            * São os serviços em andamento sem nada emitido e sem nada a emitir.
            * Eles não têm ação financeira, e por isso saíram da lista — mas a
            * conta tem que fechar: uma tela que mostra seis de vinte e quatro
            * serviços sem dizer onde estão os outros dezoito parece quebrada.
            */}
          {groupedServices.foraDoFinanceiro > 0 && (
            <p className="text-xs text-slate-500 px-1">
              {groupedServices.foraDoFinanceiro} serviço
              {groupedServices.foraDoFinanceiro !== 1 ? 's' : ''} em andamento, sem nada
              faturado nem a faturar. {canEdit() ? 'Eles aparecem no quadro de serviços.' : ''}
            </p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm">
          <div className="text-center py-12 text-slate-500">
            <DollarSign className="mx-auto mb-4 text-slate-400" size={48} />
            <p className="text-lg">Nenhum serviço cadastrado</p>
            <p className="text-sm mt-2">Cadastre serviços para iniciar o controle financeiro</p>
          </div>
        </div>
      )}
    </div>
  );
};

/** Sem acento e sem caixa: "jose" acha "JOSÉ". */
const semAcento = (v) => String(v ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().trim();

/** "out/2026" a partir de "2026-10", sem passar por Date e seu fuso. */
const nomeDoMes = (mes) => {
  const [ano, m] = mes.split('-');
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[Number(m) - 1]}/${ano}`;
};

/** Dinheiro como o brasileiro lê. Local ao painel, que é quem usa. */
const emReais = (v) => Number(v || 0).toLocaleString('pt-BR',
  { style: 'currency', currency: 'BRL' });

/**
 * Quanto ainda falta entrar, e de quem.
 *
 * Fica no topo do Financeiro porque é a pergunta que traz a pessoa aqui. Os
 * grupos abaixo respondem "o que fazer com cada serviço"; isto responde "quanto
 * o laboratório tem a receber" — e sem esta linha era preciso somar serviço por
 * serviço de cabeça.
 *
 * `vencido` sai separado do saldo, e não dentro dele: são números de urgência
 * diferente, e um saldo alto com nada vencido é uma situação saudável que não
 * pode parecer um problema.
 *
 * TRÊS NÚMEROS, e não seis. Faturado ao cliente, os três livros do período e
 * a barra de "quando entra" foram para o relatório. O RECEBIDO voltou, com os
 * meses dentro dele: sem ele, a tela só sabia dizer o que falta.
 *
 * `recarregar` existe porque estes números ficavam CONGELADOS no estado de
 * quando a aba montou. Faturar, refaturar, lançar recebimento e quitar
 * recarregam os serviços e as parcelas, e nenhum deles tocava aqui: os cartões
 * atualizavam e o painel logo acima continuava mostrando o mundo de minutos
 * atrás, lado a lado.
 */
const PainelDeSaldo = ({ laboratoryId, recarregar = 0, clientes = [], entrada = 0 }) => {
  const [linhas, setLinhas] = useState([]);
  const [emAberto, setEmAberto] = useState([]);
  const [porMes, setPorMes] = useState([]);
  const [mesesAbertos, setMesesAbertos] = useState(false);
  // Fechado ao entrar, como os grupos abaixo. O botão fechado continua dizendo
  // quantos devem e quantos têm parcela vencida.
  const [aberto, setAberto] = useState(false);
  useEffect(() => { setAberto(false); setMesesAbertos(false); }, [entrada]);

  useEffect(() => {
    if (!laboratoryId) return;
    let cancelado = false;
    Promise.all([
      listarSaldoPorCliente(laboratoryId),
      listarParcelasEmAberto(laboratoryId),
    ])
      .then(([saldo, parcelas]) => {
        if (cancelado) return;
        setLinhas(saldo || []);
        setEmAberto(parcelas || []);
      })
      // Tolerante: sem estes números o resto do Financeiro continua servindo.
      .catch(e => console.error('saldo por cliente:', e));
    // Separado: sem os meses, o painel ainda mostra o resto.
    listarRecebidoNoTempo(laboratoryId)
      .then(rows => { if (!cancelado) setPorMes(recebidoPorMes(rows || [])); })
      .catch(e => console.error('recebido por mês:', e));
    return () => { cancelado = true; };
  }, [laboratoryId, recarregar]);

  const t = resumoDoSaldo(linhas);
  const agenda = agendaDeRecebimento(emAberto);
  /**
   * Vencido primeiro, depois o maior saldo.
   *
   * Ordenava só por saldo, e cobrança não é sobre quem deve mais — é sobre o
   * que está envelhecendo. Um saldo alto que vence em novembro não pede
   * telefonema hoje; R$ 300 vencidos há vinte dias pedem.
   */
  const devedores = linhas
    .filter(l => Number(l.saldo) > 0)
    .sort((a, b) => (Number(b.vencido) || 0) - (Number(a.vencido) || 0)
      || Number(b.saldo) - Number(a.saldo));

  const comVencido = devedores.filter(l => Number(l.vencido) > 0).length;
  const contatoDoCliente = (id) => clientes.find(c => c.id === id)?.contact || null;

  if (linhas.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg">
      <div className="p-4 flex flex-wrap items-baseline gap-x-8 gap-y-3">
        <div>
          <p className="text-xs text-slate-500">A receber</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums">{emReais(t.saldo)}</p>
          <p className="text-xs text-slate-400">{t.clientes} cliente(s)</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Vencido</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums">
            {emReais(agenda.vencido)}{agenda.vencido > 0 ? ` ${MARK.WARN}` : ''}
          </p>
          <p className="text-xs text-slate-400">por parcela, não por fatura</p>
        </div>
        {/* RECEBIDO, e de quando. O total é o mesmo da view do saldo; os meses
            saem da data de cada recebimento e abrem no próprio número, em vez
            de virarem outra faixa na tela. */}
        <div>
          <p className="text-xs text-slate-500">Recebido</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums">{emReais(t.recebido)}</p>
          {porMes.length > 0 && (
            <button onClick={() => setMesesAbertos(a => !a)}
              className="text-xs text-slate-500 hover:text-slate-700 underline decoration-dotted">
              {mesesAbertos ? 'esconder meses' : 'por mês'}
            </button>
          )}
          {mesesAbertos && (
            <div className="mt-1 space-y-0.5">
              {porMes.map(m => (
                <p key={m.mes} className="text-xs text-slate-600 flex justify-between gap-4 tabular-nums">
                  <span>{nomeDoMes(m.mes)}</span>
                  <span>{emReais(m.valor)}</span>
                </p>
              ))}
            </div>
          )}
        </div>
        {/* Parcela sem vencimento não vence nunca: ela sumia de toda cobrança
            em vez de aparecer no fim da fila. */}
        {agenda.semData > 0 && (
          <p className="text-xs text-slate-700 self-center">
            {MARK.WARN} {emReais(agenda.semData)} em parcela sem vencimento. Fatura sem data não
            entra em cobrança nenhuma.
          </p>
        )}
      </div>

      {/* ── QUEM DEVE: a tela de cobrança ──────────────────────────────
          Era nome e valor, e cobrar exige mais que isso. Quem vai ligar
          precisa saber o que já venceu, quando vence o próximo, e com quem
          falar — o telefone estava no cadastro e não chegava aqui.

          Ordenado pelo VENCIDO primeiro, não pelo maior saldo: o que envelhece
          é o que se perde, e um saldo grande que vence em novembro não pede
          telefonema hoje. */}
      {devedores.length > 0 && (
        <div className="border-t border-slate-100">
          <button onClick={() => setAberto(a => !a)}
            className="w-full px-4 py-2 flex items-center justify-between text-sm text-slate-600 hover:bg-slate-50">
            <span>{aberto ? 'Esconder' : 'Ver'} quem deve ({devedores.length})</span>
            {!aberto && comVencido > 0 && (
              <span className="text-xs text-slate-700">
                {MARK.WARN} {comVencido} com parcela vencida
              </span>
            )}
          </button>

          {aberto && (
            <div className="divide-y divide-slate-100">
              {devedores.map(l => {
                const contato = contatoDoCliente(l.client_id);
                const vencido = Number(l.vencido) || 0;
                const venceEm = l.proximo_vencimento
                  ? new Date(`${String(l.proximo_vencimento).slice(0, 10)}T00:00:00`)
                    .toLocaleDateString('pt-BR')
                  : null;
                return (
                  <div key={l.client_id || l.client_name} className="px-4 py-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800 truncate">
                          {l.client_name || 'Sem cliente'}
                        </p>
                        {contato && (
                          <p className="text-xs text-slate-500 truncate">{contato}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-slate-900 tabular-nums">
                          {emReais(l.saldo)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {vencido > 0
                            ? <span className="text-slate-700">{MARK.WARN} {emReais(vencido)} vencido</span>
                            : venceEm ? `vence ${venceEm}` : 'sem vencimento'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinancialTab;
